/**
 * Centralized Unicode glyph mappings for planets, zodiac signs, and aspects.
 * Used across report, chart UI, and symbol legend.
 * Always keep text labels visible for beginner readability.
 */

export const PLANET_GLYPHS = {
  sun: '☉',
  moon: '☽',
  mercury: '☿',
  venus: '♀',
  mars: '♂',
  jupiter: '♃',
  saturn: '♄',
  uranus: '♅',
  neptune: '♆',
  pluto: '♇',
  northnode: '☊',
  lilith: '⚸',
  chiron: '⚷',
};

export const SIGN_GLYPHS = {
  Aries: '♈',
  Taurus: '♉',
  Gemini: '♊',
  Cancer: '♋',
  Leo: '♌',
  Virgo: '♍',
  Libra: '♎',
  Scorpio: '♏',
  Sagittarius: '♐',
  Capricorn: '♑',
  Aquarius: '♒',
  Pisces: '♓',
};

export const ASPECT_GLYPHS = {
  conjunction: '☌',
  opposition: '☍',
  square: '□',
  trine: '△',
  sextile: '✶',
};

export const PLANET_LABELS = {
  sun: 'Sun',
  moon: 'Moon',
  mercury: 'Mercury',
  venus: 'Venus',
  mars: 'Mars',
  jupiter: 'Jupiter',
  saturn: 'Saturn',
  uranus: 'Uranus',
  neptune: 'Neptune',
  pluto: 'Pluto',
  northnode: 'North Node',
  lilith: 'Lilith',
  chiron: 'Chiron',
};

/** Format: ☉ Sun — ♎ Libra — 5th House */
export function formatPlacementWithGlyph(placement, ordinalHouse) {
  if (!placement) return '';
  const key = String(placement.key || '').toLowerCase();
  const planetGlyph = PLANET_GLYPHS[key] || '';
  const planetName = PLANET_LABELS[key] || placement.name || placement.key || '';
  const sign = placement.sign || '';
  const signGlyph = sign ? (SIGN_GLYPHS[sign] || '') : '';
  const house = placement.house;
  const housePart = Number.isFinite(Number(house)) ? ` — ${ordinalHouse(house)} House` : '';
  const signPart = sign ? ` — ${signGlyph} ${sign}` : '';
  return `${planetGlyph} ${planetName}${signPart}${housePart}`.trim();
}

/** Format: ☉ Sun ☌ ☿ Mercury */
export function formatAspectWithGlyph(p1Key, p2Key, type, p1Label, p2Label) {
  const g1 = PLANET_GLYPHS[String(p1Key || '').toLowerCase()] || '';
  const g2 = PLANET_GLYPHS[String(p2Key || '').toLowerCase()] || '';
  const gAspect = ASPECT_GLYPHS[String(type || '').toLowerCase()] || '';
  const n1 = p1Label || PLANET_LABELS[String(p1Key || '').toLowerCase()] || p1Key;
  const n2 = p2Label || PLANET_LABELS[String(p2Key || '').toLowerCase()] || p2Key;
  return `${g1} ${n1} ${gAspect} ${g2} ${n2}`;
}

export function getPlanetGlyph(key) {
  return PLANET_GLYPHS[String(key || '').toLowerCase()] || '';
}

export function getSignGlyph(sign) {
  return sign ? (SIGN_GLYPHS[sign] || '') : '';
}

export function getAspectGlyph(type) {
  return ASPECT_GLYPHS[String(type || '').toLowerCase()] || '';
}

/** Symbol legend content for "Understanding the Symbols" section */
export const SYMBOL_LEGEND = {
  planets: [
    { glyph: '☉', name: 'Sun', desc: 'core identity and vitality' },
    { glyph: '☽', name: 'Moon', desc: 'emotional nature and instinct' },
    { glyph: '☿', name: 'Mercury', desc: 'thought, language, perception' },
    { glyph: '♀', name: 'Venus', desc: 'relating, attraction, values' },
    { glyph: '♂', name: 'Mars', desc: 'action, drive, assertion' },
    { glyph: '♃', name: 'Jupiter', desc: 'growth, faith, expansion' },
    { glyph: '♄', name: 'Saturn', desc: 'structure, limits, responsibility' },
    { glyph: '♅', name: 'Uranus', desc: 'disruption, change, awakening' },
    { glyph: '♆', name: 'Neptune', desc: 'imagination, sensitivity, diffusion' },
    { glyph: '♇', name: 'Pluto', desc: 'intensity, power, transformation' },
  ],
  signs: [
    { glyph: '♈', name: 'Aries', desc: 'initiative and direct action' },
    { glyph: '♉', name: 'Taurus', desc: 'steadiness and material grounding' },
    { glyph: '♊', name: 'Gemini', desc: 'curiosity and communication' },
    { glyph: '♋', name: 'Cancer', desc: 'care, protection, emotional sensitivity' },
    { glyph: '♌', name: 'Leo', desc: 'visibility and creative self-expression' },
    { glyph: '♍', name: 'Virgo', desc: 'refinement, analysis, discernment' },
    { glyph: '♎', name: 'Libra', desc: 'balance, proportion, relationship' },
    { glyph: '♏', name: 'Scorpio', desc: 'depth, intensity, transformation' },
    { glyph: '♐', name: 'Sagittarius', desc: 'exploration, meaning, expansion' },
    { glyph: '♑', name: 'Capricorn', desc: 'structure, control, long-term effort' },
    { glyph: '♒', name: 'Aquarius', desc: 'perspective, difference, innovation' },
    { glyph: '♓', name: 'Pisces', desc: 'permeability, imagination, compassion' },
  ],
  aspects: [
    { glyph: '☌', name: 'Conjunction', desc: 'functions merge or intensify' },
    { glyph: '☍', name: 'Opposition', desc: 'tension through polarity' },
    { glyph: '□', name: 'Square', desc: 'friction that pushes development' },
    { glyph: '△', name: 'Trine', desc: 'ease, coherence, natural flow' },
    { glyph: '✶', name: 'Sextile', desc: 'opportunity, cooperation, activation' },
  ],
};
